import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "ruleType", "dateFields", "dateInputs", "indefiniteToggle", "validationMessage", "preview",
    "weekendFields", "holidayFields", "bookingRequirementsFields", "minimumStay", "indefiniteHelpText"
  ]

  connect() {
    this.toggleRuleSpecificFields()
    this.updatePreview()
  }

  toggleDates() {
    this.toggleRuleSpecificFields()
    this.updatePreview()
  }

  toggleIndefinite(event) {
    // Only toggle if not a weekend rule
    if (this.ruleTypeTarget.value !== 'weekend') {
      const showDates = !event.target.checked
      this.dateInputsTarget.classList.toggle('hidden', !showDates)
      this.updatePreview()
    }
  }

  toggleRuleSpecificFields() {
    const ruleType = this.ruleTypeTarget.value

    // Hide all rule-specific field sections
    this.hideAllRuleFields()
    
    // Show the appropriate section based on rule type
    if (ruleType === 'weekend') {
      this.weekendFieldsTarget.classList.remove('hidden')
      if (this.hasBookingRequirementsFieldsTarget) {
        this.bookingRequirementsFieldsTarget.classList.remove('hidden')
      }
      
      // For weekend rules, automatically set to indefinite (always in effect)
      if (this.hasIndefiniteToggleTarget) {
        this.indefiniteToggleTarget.checked = true
        this.indefiniteToggleTarget.disabled = true
        this.dateInputsTarget.classList.add('hidden')
        
        // Update the help text for indefinite toggle
        if (this.hasIndefiniteHelpTextTarget) {
          this.indefiniteHelpTextTarget.textContent = 'Weekend rules are always active year-round'
        }
      }
    } else if (ruleType === 'holiday') {
      this.holidayFieldsTarget.classList.remove('hidden')
      if (this.hasBookingRequirementsFieldsTarget) {
        this.bookingRequirementsFieldsTarget.classList.remove('hidden')
      }
      
      // For holiday rules, enable the indefinite toggle
      if (this.hasIndefiniteToggleTarget) {
        this.indefiniteToggleTarget.disabled = false
        
        // Update the help text for indefinite toggle
        if (this.hasIndefiniteHelpTextTarget) {
          this.indefiniteHelpTextTarget.textContent = 'Rule applies indefinitely without specific dates'
        }
      }
    } else {
      // For other rule types, enable the indefinite toggle
      if (this.hasIndefiniteToggleTarget) {
        this.indefiniteToggleTarget.disabled = false
        
        // Update the help text for indefinite toggle
        if (this.hasIndefiniteHelpTextTarget) {
          this.indefiniteHelpTextTarget.textContent = 'Rule applies indefinitely without specific dates'
        }
      }
    }
    
    // Update the date fields visibility based on rule type
    this.updateDateFieldsVisibility(ruleType)
  }

  hideAllRuleFields() {
    // Hide all rule-specific fields
    if (this.hasWeekendFieldsTarget) {
      this.weekendFieldsTarget.classList.add('hidden')
    }
    
    if (this.hasHolidayFieldsTarget) {
      this.holidayFieldsTarget.classList.add('hidden')
    }
    
    if (this.hasBookingRequirementsFieldsTarget) {
      this.bookingRequirementsFieldsTarget.classList.add('hidden')
    }
  }

  updateDateFieldsVisibility(ruleType) {
    // For weekend rules, always show date fields container but hide the inputs
    if (ruleType === 'weekend') {
      this.dateFieldsTarget.classList.remove('hidden')
    } else {
      // For other rule types, show date fields by default
      this.dateFieldsTarget.classList.remove('hidden')
    }
  }

  validateDates() {
    const startDateInput = this.element.querySelector('[name="pricing_rule[start_date]"]')
    const endDateInput = this.element.querySelector('[name="pricing_rule[end_date]"]')
    
    if (!startDateInput || !endDateInput) return true
    
    const startDate = startDateInput.value ? new Date(startDateInput.value) : null
    const endDate = endDateInput.value ? new Date(endDateInput.value) : null
    
    // Clear previous validation message
    this.validationMessageTarget.textContent = ''
    this.validationMessageTarget.classList.add('hidden')
    
    // If either date is not set, no validation needed yet
    if (!startDate || !endDate) return true
    
    // Check if end date is after start date
    if (endDate < startDate) {
      this.validationMessageTarget.textContent = 'End date must be after start date'
      this.validationMessageTarget.classList.remove('hidden')
      return false
    }
    
    return true
  }

  updatePreview() {
    // Get current values
    const ruleType = this.ruleTypeTarget.value
    const indefinite = this.indefiniteToggleTarget.checked
    
    let previewText = ''
    
    // Build preview text based on rule type
    switch (ruleType) {
      case 'weekend':
        previewText = this.buildWeekendRulePreview(true) // Always indefinite for weekend rules
        break
      case 'holiday':
        previewText = this.buildHolidayRulePreview(indefinite)
        break
      case 'seasonal':
        previewText = 'Seasonal pricing applies during the specified season dates'
        break
      case 'special':
        previewText = 'Special pricing applies during the specified promotion period'
        break
      case 'event':
        previewText = 'Event pricing applies during the specified event dates'
        break
      default:
        previewText = 'Select rule type and dates to see preview'
    }
    
    this.previewTarget.textContent = previewText
  }

  buildWeekendRulePreview(indefinite) {
    // Get selected days of week
    const dayCheckboxes = this.element.querySelectorAll('input[name="pricing_rule[days_of_week][]"]:checked')
    const selectedDays = Array.from(dayCheckboxes).map(checkbox => {
      const dayIndex = parseInt(checkbox.value)
      return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayIndex]
    })
    
    // Get minimum stay
    const minimumStay = this.hasMinimumStayTarget ? parseInt(this.minimumStayTarget.value) || 0 : 0
    
    // Get booking pattern
    const bookingPatternSelect = this.element.querySelector('[name="pricing_rule[booking_pattern]"]')
    const bookingPattern = bookingPatternSelect ? bookingPatternSelect.selectedOptions[0].text : 'Any'
    
    // Build the preview text
    let preview = ''
    
    if (selectedDays.length > 0) {
      preview += `Weekend pricing applies on ${selectedDays.join(', ')}`
    } else {
      preview += 'Weekend pricing applies on selected days'
    }
    
    // For weekend rules, always show as indefinite
    preview += ' year-round'
    
    if (minimumStay > 0) {
      preview += `. Requires minimum stay of ${minimumStay} night${minimumStay !== 1 ? 's' : ''}`
    }
    
    if (bookingPattern && bookingPattern !== 'Any') {
      preview += `. Booking pattern: ${bookingPattern}`
    }
    
    return preview
  }
  
  buildHolidayRulePreview(indefinite) {
    // Get holiday name
    const holidayNameInput = this.element.querySelector('[name="pricing_rule[holiday_name]"]')
    const holidayName = holidayNameInput && holidayNameInput.value ? holidayNameInput.value : 'the holiday'
    
    // Get buffer days
    const bufferBeforeInput = this.element.querySelector('[name="pricing_rule[buffer_days_before]"]')
    const bufferAfterInput = this.element.querySelector('[name="pricing_rule[buffer_days_after]"]')
    const bufferBefore = bufferBeforeInput ? parseInt(bufferBeforeInput.value) || 0 : 0
    const bufferAfter = bufferAfterInput ? parseInt(bufferAfterInput.value) || 0 : 0
    
    // Get minimum stay
    const minimumStay = this.hasMinimumStayTarget ? parseInt(this.minimumStayTarget.value) || 0 : 0
    
    // Get booking pattern
    const bookingPatternSelect = this.element.querySelector('[name="pricing_rule[booking_pattern]"]')
    const bookingPattern = bookingPatternSelect ? bookingPatternSelect.selectedOptions[0].text : 'Any'
    
    // Get dates
    let dateText = ''
    if (indefinite) {
      dateText = `every year`
    } else {
      const startDateInput = this.element.querySelector('[name="pricing_rule[start_date]"]')
      const endDateInput = this.element.querySelector('[name="pricing_rule[end_date]"]')
      
      if (startDateInput && startDateInput.value && endDateInput && endDateInput.value) {
        const startDate = new Date(startDateInput.value)
        const endDate = new Date(endDateInput.value)
        
        dateText = `from ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`
      } else if (startDateInput && startDateInput.value) {
        const startDate = new Date(startDateInput.value)
        dateText = `starting from ${startDate.toLocaleDateString()}`
      } else if (endDateInput && endDateInput.value) {
        const endDate = new Date(endDateInput.value)
        dateText = `until ${endDate.toLocaleDateString()}`
      } else {
        dateText = 'when dates are specified'
      }
    }
    
    // Build the preview text
    let preview = `Holiday pricing applies for ${holidayName} ${dateText}`
    
    // Add buffer days info
    if (bufferBefore > 0 || bufferAfter > 0) {
      preview += ` (including `
      
      if (bufferBefore > 0) {
        preview += `${bufferBefore} day${bufferBefore !== 1 ? 's' : ''} before`
      }
      
      if (bufferBefore > 0 && bufferAfter > 0) {
        preview += ` and `
      }
      
      if (bufferAfter > 0) {
        preview += `${bufferAfter} day${bufferAfter !== 1 ? 's' : ''} after`
      }
      
      preview += `)`
    }
    
    // Add booking requirements
    if (minimumStay > 0) {
      preview += `. Requires minimum stay of ${minimumStay} night${minimumStay !== 1 ? 's' : ''}`
    }
    
    if (bookingPattern && bookingPattern !== 'Any') {
      preview += `. Booking pattern: ${bookingPattern}`
    }
    
    return preview
  }
}
